/**
 * ThreeJsViewer — wraps a Three.js scene for GLTF/GLB model rendering.
 *
 * Responsibilities:
 *  - Load a GLB file into a scene
 *  - Accept per-mesh color overrides (for color pickers)
 *  - Accept per-mesh visibility toggles (for option toggles)
 *  - Provide a screenshot() method for PDF generation
 *  - Clean up WebGL resources on destroy()
 */

import * as THREE from 'three';
import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

export class ThreeJsViewer {
    private renderer: THREE.WebGLRenderer;
    private scene: THREE.Scene;
    private camera: THREE.PerspectiveCamera;
    private meshMap: Map<string, THREE.Mesh[]> = new Map();
    private controls: OrbitControls | null = null;
    private shadowLight: THREE.DirectionalLight | null = null;
    private groundPlane: THREE.Mesh | null = null;
    private animFrameId: number | null = null;
    private resizeObserver: ResizeObserver | null = null;

    constructor(private readonly canvas: HTMLCanvasElement) {
        this.renderer = new THREE.WebGLRenderer({
            canvas,
            antialias: true,
            alpha: true,
            /** preserveDrawingBuffer is required for screenshot() to work after rendering */
            preserveDrawingBuffer: true,
        });
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;

        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0x2a334e);

        this.camera = new THREE.PerspectiveCamera(45, canvas.width / canvas.height, 0.01, 1000);
        this.camera.position.set(0, 1, 3);

        // Shadow maps — PCFSoft gives smooth, blurred shadow edges
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

        // Ambient — kept low so the key light dominates
        this.scene.add(new THREE.AmbientLight(0xffffff, 0.35));

        // Key light — strong, upper front-right; the primary shadow caster
        const keyLight = new THREE.DirectionalLight(0xffffff, 2.2);
        keyLight.position.set(5, 10, 7);
        keyLight.castShadow = true;
        keyLight.shadow.mapSize.width  = 2048;
        keyLight.shadow.mapSize.height = 2048;
        keyLight.shadow.bias = -0.001;
        this.scene.add(keyLight);
        this.shadowLight = keyLight;

        // Fill light — cool blue tint from opposite side, softens hard shadows
        const fillLight = new THREE.DirectionalLight(0x8899cc, 0.6);
        fillLight.position.set(-6, 3, -4);
        this.scene.add(fillLight);

        // Orbit controls — left-drag to rotate, right-drag to pan, scroll to zoom
        this.controls = new OrbitControls(this.camera, canvas);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.08;

        // Track canvas size changes with ResizeObserver for responsive rendering
        this.resizeObserver = new ResizeObserver(() => this.handleResize());
        this.resizeObserver.observe(canvas);
        this.handleResize();
    }

    /** Load a GLB model. Clears any previously loaded model. */
    async loadModel(url: string): Promise<void> {
        // Remove old model from scene
        const prev = this.scene.getObjectByName('__vc_model__');
        if (prev) this.scene.remove(prev);
        this.meshMap.clear();

        // Fetch the GLB binary manually and strip all texture/image/sampler
        // entries from its JSON chunk before parsing. This prevents GLTFLoader
        // from ever requesting external texture files or logging load errors.
        const response = await fetch(url);
        const rawBuffer = await response.arrayBuffer();
        const cleanBuffer = ThreeJsViewer.stripGlbTextures(rawBuffer);

        const loader = new GLTFLoader();
        const gltf = await new Promise<GLTF>((resolve, reject) => {
            loader.parse(cleanBuffer, '', resolve, reject);
        });
        const root = gltf.scene;
        root.name = '__vc_model__';

        // Collect named meshes and strip embedded/external textures.
        // We drive all colors programmatically, so textures are not needed and
        // external texture references (e.g. Textures/colormap.png) would 404.
        root.traverse((node) => {
            if (!(node as THREE.Mesh).isMesh) return;
            const mesh = node as THREE.Mesh;

            // Give every mesh its own cloned material so that setPartColor() on
            // one mesh doesn't bleed into other meshes that share the same material
            // instance (very common in exported GLBs).
            const isolateMaterial = (mat: THREE.Material): THREE.Material => {
                const std = mat as THREE.MeshStandardMaterial;
                // Always create a fresh MeshStandardMaterial — this both isolates
                // the instance and strips any leftover texture references.
                const isolated = new THREE.MeshStandardMaterial({
                    color: std.color?.clone() ?? new THREE.Color(0xcccccc),
                    roughness: std.roughness ?? 0.8,
                    metalness: std.metalness ?? 0.1,
                    side: mat.side,
                });
                // Don't dispose the original — other meshes may still reference it
                // at this point in the traversal; Three.js will GC it once unused.
                return isolated;
            };

            if (Array.isArray(mesh.material)) {
                mesh.material = mesh.material.map(isolateMaterial);
            } else {
                mesh.material = isolateMaterial(mesh.material);
            }

            if (mesh.name) {
                const existing = this.meshMap.get(mesh.name) ?? [];
                existing.push(mesh);
                this.meshMap.set(mesh.name, existing);
            }
        });

        this.scene.add(root);
        this.fitCameraToModel(root);
        this.setupGroundAndShadows(root);
        if (this.controls) this.controls.target.copy(
            new THREE.Box3().setFromObject(root).getCenter(new THREE.Vector3())
        );
        this.startRenderLoop();
    }

    /**
     * Rewrite the JSON chunk of a GLB binary to remove all texture/image/sampler
     * entries and their references from materials. Returns the cleaned ArrayBuffer.
     * If the input is not a valid GLB the original buffer is returned unchanged.
     */
    private static stripGlbTextures(buffer: ArrayBuffer): ArrayBuffer {
        const view = new DataView(buffer);

        // GLB magic: 'glTF' = 0x46546C67
        if (view.byteLength < 20 || view.getUint32(0, true) !== 0x46546C67) return buffer;

        const jsonChunkLength = view.getUint32(12, true);
        const jsonChunkType   = view.getUint32(16, true);
        if (jsonChunkType !== 0x4E4F534A) return buffer; // first chunk must be JSON

        // Parse JSON chunk
        const jsonBytes = new Uint8Array(buffer, 20, jsonChunkLength);
        const json = JSON.parse(new TextDecoder().decode(jsonBytes)) as Record<string, unknown>;

        // Remove texture-related top-level arrays
        delete json.textures;
        delete json.images;
        delete json.samplers;

        // Remove texture references from all materials
        type GltfMaterial = {
            pbrMetallicRoughness?: {
                baseColorTexture?: unknown;
                metallicRoughnessTexture?: unknown;
            };
            normalTexture?: unknown;
            occlusionTexture?: unknown;
            emissiveTexture?: unknown;
        };
        for (const mat of (json.materials ?? []) as GltfMaterial[]) {
            if (mat.pbrMetallicRoughness) {
                delete mat.pbrMetallicRoughness.baseColorTexture;
                delete mat.pbrMetallicRoughness.metallicRoughnessTexture;
            }
            delete mat.normalTexture;
            delete mat.occlusionTexture;
            delete mat.emissiveTexture;
        }

        // Re-encode JSON, padded to 4-byte boundary with spaces (GLTF spec requires 0x20)
        const reencoded  = new TextEncoder().encode(JSON.stringify(json));
        const paddedLen  = Math.ceil(reencoded.length / 4) * 4;
        const newJson    = new Uint8Array(paddedLen).fill(0x20);
        newJson.set(reencoded);

        // Preserve everything after the original JSON chunk (binary buffer chunk, etc.)
        const restOffset = 20 + jsonChunkLength;
        const restLength = buffer.byteLength - restOffset;
        const newTotal   = 12 + 8 + paddedLen + restLength;

        const out      = new ArrayBuffer(newTotal);
        const outDV    = new DataView(out);
        const outBytes = new Uint8Array(out);

        outDV.setUint32(0, 0x46546C67, true); // magic
        outDV.setUint32(4, 2,          true); // version
        outDV.setUint32(8, newTotal,   true); // total length

        outDV.setUint32(12, paddedLen,   true); // JSON chunk length
        outDV.setUint32(16, 0x4E4F534A, true); // JSON chunk type
        outBytes.set(newJson, 20);

        if (restLength > 0) {
            outBytes.set(new Uint8Array(buffer, restOffset, restLength), 20 + paddedLen);
        }

        return out;
    }

    /**
     * Override the color of all mesh surfaces associated with the given mesh name.
     * hexColor — CSS hex string, e.g. '#ff0000' or 'ff0000'.
     */
    setPartColor(meshName: string, hexColor: string): void {
        const meshes = this.meshMap.get(meshName);
        if (!meshes?.length) return;

        const color = new THREE.Color(hexColor.startsWith('#') ? hexColor : `#${hexColor}`);
        for (const mesh of meshes) {
            const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
            for (const mat of materials) {
                if ((mat as THREE.MeshStandardMaterial).color !== undefined) {
                    (mat as THREE.MeshStandardMaterial).color.set(color);
                    (mat as THREE.MeshStandardMaterial).needsUpdate = true;
                }
            }
        }
    }

    /** Show or hide all meshes with the given name. */
    setPartVisible(meshName: string, visible: boolean): void {
        const meshes = this.meshMap.get(meshName);
        if (!meshes?.length) return;
        for (const mesh of meshes) {
            mesh.visible = visible;
        }
    }

    /**
     * Capture the current frame as a data URI (PNG).
     * Requires preserveDrawingBuffer: true (set in constructor).
     */
    screenshot(): string {
        // Render one last frame to make sure the buffer is fresh
        this.renderer.render(this.scene, this.camera);
        return this.canvas.toDataURL('image/png');
    }

    /** Release all Three.js GPU resources and stop the render loop. */
    destroy(): void {
        this.stopRenderLoop();
        this.controls?.dispose();
        this.controls = null;
        if (this.groundPlane) {
            this.groundPlane.geometry.dispose();
            (this.groundPlane.material as THREE.Material).dispose();
            this.groundPlane = null;
        }
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;

        this.scene.traverse((node) => {
            if ((node as THREE.Mesh).isMesh) {
                const mesh = node as THREE.Mesh;
                mesh.geometry?.dispose();
                const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
                for (const mat of mats) mat?.dispose();
            }
        });

        this.meshMap.clear();
        this.renderer.dispose();
    }

    // ── Private helpers ───────────────────────────────────────────────────

    /**
     * Creates a ShadowMaterial ground plane at the model's base and tightens the
     * shadow camera frustum so the full 2048px shadow map is used on the model.
     * ShadowMaterial is fully transparent except where shadows fall, so the dark
     * navy background shows through and the shadow appears as a soft pool.
     */
    private setupGroundAndShadows(root: THREE.Object3D): void {
        // Remove previous ground plane if reloading a model
        if (this.groundPlane) {
            this.scene.remove(this.groundPlane);
            this.groundPlane.geometry.dispose();
            (this.groundPlane.material as THREE.Material).dispose();
            this.groundPlane = null;
        }

        const box    = new THREE.Box3().setFromObject(root);
        const size   = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);

        // Every mesh in the model casts a shadow onto the ground
        root.traverse((node) => {
            if ((node as THREE.Mesh).isMesh) (node as THREE.Mesh).castShadow = true;
        });

        // Tighten the shadow camera to the model bounds for maximum shadow resolution
        if (this.shadowLight) {
            const halfSize = maxDim * 1.5;
            const cam = this.shadowLight.shadow.camera as THREE.OrthographicCamera;
            cam.left   = -halfSize;
            cam.right  =  halfSize;
            cam.top    =  halfSize;
            cam.bottom = -halfSize;
            cam.near   = 0.1;
            cam.far    = maxDim * 20;
            cam.updateProjectionMatrix();

            // Position light above/beside the model and aim at its centre
            this.shadowLight.position.set(
                center.x + maxDim * 1.5,
                center.y + maxDim * 2.5,
                center.z + maxDim * 1.5,
            );
            this.shadowLight.target.position.copy(center);
            if (!this.shadowLight.target.parent) {
                this.scene.add(this.shadowLight.target);
            }
        }

        // Ground plane — ShadowMaterial is invisible except where shadows land,
        // so the navy background bleeds through and the shadow is the only mark.
        const groundSize = maxDim * 5;
        const ground = new THREE.Mesh(
            new THREE.PlaneGeometry(groundSize, groundSize),
            new THREE.ShadowMaterial({ opacity: 0.45, transparent: true }),
        );
        ground.rotation.x = -Math.PI / 2;
        ground.position.set(center.x, box.min.y, center.z);
        ground.receiveShadow = true;
        ground.name = '__vc_ground__';
        this.scene.add(ground);
        this.groundPlane = ground;
    }

    private fitCameraToModel(model: THREE.Object3D): void {
        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());

        const maxDim = Math.max(size.x, size.y, size.z);
        const fov = this.camera.fov * (Math.PI / 180);
        let dist = Math.abs(maxDim / (2 * Math.tan(fov / 2)));
        dist *= 1.5; // padding

        this.camera.position.set(center.x, center.y + size.y * 0.25, center.z + dist);
        this.camera.lookAt(center);
        this.camera.near = dist / 100;
        this.camera.far = dist * 100;
        this.camera.updateProjectionMatrix();
    }

    private handleResize(): void {
        const w = this.canvas.clientWidth || this.canvas.width;
        const h = this.canvas.clientHeight || this.canvas.height;
        this.renderer.setSize(w, h, false);
        this.camera.aspect = w / h;
        this.camera.updateProjectionMatrix();
    }

    private startRenderLoop(): void {
        if (this.animFrameId !== null) return;
        const loop = (): void => {
            this.animFrameId = requestAnimationFrame(loop);
            this.controls?.update(); // required for damping
            this.renderer.render(this.scene, this.camera);
        };
        loop();
    }

    private stopRenderLoop(): void {
        if (this.animFrameId !== null) {
            cancelAnimationFrame(this.animFrameId);
            this.animFrameId = null;
        }
    }
}
